import iconv from "iconv-lite"

/**
 * ESC/POS Command Encoder
 * Used to generate raw byte commands for thermal printers
 */
export class EscPosEncoder {
  buffer: number[]
  private lineFeedGapDots = 0

  constructor() {
    this.buffer = []
  }

  /**
   * Initialize printer
   */
  initialize() {
    this.buffer.push(0x1B, 0x40)
    return this
  }

  /**
   * Semantic marker used by the optional raster recorder.
   * Native ESC/POS output does not need an extra command.
   */
  textRole(_role: "title" | "body") {
    return this
  }

  /**
   * Add text (Supports GBK via iconv-lite for Chinese characters)
   * @param content Text content
   */
  text(content: string) {
    // Check if iconv works differently in browser vs node,
    // but typically vite polyfills or we use a browser-compatible import if needed.
    // Standard iconv-lite usually works if built correctly.
    const encoded = iconv.encode(content, "gbk")
    // encoded is a Buffer, we need to push bytes to our number array
    for (let i = 0; i < encoded.length; i++) {
      const byte = encoded[i]
      this.buffer.push(byte)
      if (byte === 0x0A) {
        this.appendLineFeedGap()
      }
    }
    return this
  }

  /**
   * Align text
   * @param align 'LEFT' | 'CENTER' | 'RIGHT'
   */
  align(align: 'LEFT' | 'CENTER' | 'RIGHT') {
    let n = 0
    if (align === "CENTER") n = 1
    if (align === "RIGHT") n = 2
    this.buffer.push(0x1B, 0x61, n)
    return this
  }

  /**
   * Set text size
   * @param width 0-7
   * @param height 0-7
   */
  size(width: number, height: number) {
    // GS ! n
    // or GS ( N ?
    // Usually uses GS ! n where n is bits.
    // 0x1D 0x21 n
    // low 4 bits = height, high 4 bits = width (depending on printer)
    // Actually standard ESC/POS: GS ! n.
    // 0 = normal.
    // width: 0-7, height: 0-7.
    // n = (width << 4) | height

    let finalWidth = width
    let finalHeight = height

    if (finalWidth > 7) finalWidth = 7
    if (finalHeight > 7) finalHeight = 7

    const n = (finalWidth << 4) | finalHeight
    this.buffer.push(0x1D, 0x21, n)
    return this
  }

  /**
   * Set line spacing in dots.
   * Most ESC/POS printers use 30 dots as their default line spacing.
   */
  lineSpacing(dots: number) {
    const normalized = Math.max(0, Math.min(255, Math.round(dots)))
    this.buffer.push(0x1B, 0x33, normalized)
    return this
  }

  /**
   * Feed extra dots after every line break.
   * Uses ESC J n so the effect is visible even when ESC 3 line spacing is ignored.
   */
  lineFeedGap(dots: number) {
    this.lineFeedGapDots = Math.max(0, Math.min(255, Math.round(dots)))
    return this
  }

  /**
   * Feed paper by an exact number of printer dots.
   */
  feedDots(dots: number) {
    let remaining = Math.max(0, Math.round(dots))
    while (remaining > 0) {
      const step = Math.min(255, remaining)
      this.buffer.push(0x1B, 0x4A, step)
      remaining -= step
    }
    return this
  }

  /**
   * Print a monochrome raster image using GS v 0.
   * Each set bit is printed as a black dot.
   */
  rasterImage(widthDots: number, heightDots: number, data: Uint8Array) {
    const width = Math.max(1, Math.round(widthDots))
    const height = Math.max(1, Math.round(heightDots))
    const widthBytes = Math.ceil(width / 8)
    const expectedLength = widthBytes * height
    if (data.length !== expectedLength) {
      throw new RangeError(`光栅数据长度错误：期望 ${expectedLength}，实际 ${data.length}`)
    }

    this.buffer.push(
      0x1D,
      0x76,
      0x30,
      0x00,
      widthBytes & 0xFF,
      (widthBytes >> 8) & 0xFF,
      height & 0xFF,
      (height >> 8) & 0xFF,
    )
    for (const byte of data) {
      this.buffer.push(byte)
    }
    return this
  }

  /**
   * Bold text
   * @param active boolean
   */
  bold(active: boolean) {
    this.buffer.push(0x1B, 0x45, active ? 1 : 0)
    return this
  }

  /**
   * New line
   */
  newline() {
    this.buffer.push(0x0A)
    this.appendLineFeedGap()
    return this
  }

  private appendLineFeedGap() {
    if (this.lineFeedGapDots > 0) {
      this.buffer.push(0x1B, 0x4A, this.lineFeedGapDots)
    }
  }

  /**
   * Cut paper
   */
  cut() {
    this.buffer.push(0x1D, 0x56, 0x42, 0x00)
    return this
  }

  /**
   * Convert to Base64 string
   */
  toBase64() {
    // Convert array of numbers to Uint8Array
    const uint8Array = new Uint8Array(this.buffer)
    // Use a more robust way to convert large Uint8Array to string for btoa
    let binary = ''
    const len = uint8Array.byteLength
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(uint8Array[i])
    }
    return window.btoa(binary)
  }
}
